document.addEventListener(
  "click",
  (event) => {
    const facetLink = event.target.closest("facets-drawer facet-link");
    if (!facetLink) {
      return;
    }

    const drawer = facetLink.closest("facets-drawer");
    const anchor = facetLink.querySelector("a[href]");
    const sectionId = drawer?.getAttribute("data-section-id");
    if (!anchor || !sectionId) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const url = new URL(anchor.href);
    url.searchParams.set("section_id", sectionId);

    // Sync the drawer form to the target URL, otherwise the submit triggered by
    // "dialog:after-hide" re-applies the filters we are removing
    drawer.querySelectorAll('facets-form form input[name^="filter."]').forEach((input) => {
      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = url.searchParams.getAll(input.name).includes(input.value);
      } else {
        input.value = url.searchParams.get(input.name) || "";
      }
    });

    facetLink.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url } }));
    drawer.hide();
  },
  true
);

// Collection bottom description "see more": the body is line-clamped to 8 lines by CSS, so all this does is
// check whether the text actually overflows that clamp and only then reveal the toggle that expands it.
(function () {
  function updateCollectionBottomDescriptionToggle(wrapper) {
    const body = wrapper.querySelector(".collection-bottom-description__body");
    const details = wrapper.querySelector(".collection-bottom-description__details");
    if (!body || !details) {
      return;
    }

    details.hidden = body.scrollHeight <= body.clientHeight + 1;
  }

  function initAll(root) {
    (root || document)
      .querySelectorAll(".collection-bottom-description__wrapper")
      .forEach(updateCollectionBottomDescriptionToggle);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();

// PDP description "read more": the body is line-clamped to 2 lines by CSS (.line-clamp), so all this does is
// check whether the text actually overflows that clamp and only then reveal the toggle that expands it.
(function () {
  function updateProductDescriptionToggle(wrapper) {
    const body = wrapper.querySelector(".product-description__body");
    const details = wrapper.querySelector(".product-description__details");
    if (!body || !details) {
      return;
    }

    details.hidden = body.scrollHeight <= body.clientHeight + 1;
  }

  function initAll(root) {
    (root || document).querySelectorAll(".product-description").forEach(updateProductDescriptionToggle);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  // The quick-buy modal fetches the product page and swaps its content in via `replaceChildren()`, then fires
  // "dialog:after-show" (which doesn't bubble, hence the capture-phase listener) once it's actually visible -
  // the description isn't laid out yet when the content is inserted, so scrollHeight/clientHeight would both
  // read 0 if we tried to measure it any earlier.
  document.addEventListener("dialog:after-show", function (event) {
    initAll(event.target);
  }, true);
})();

// "Shop this look" drawer: the "Select size/color" button only shows on hover (see the base
// .product-card__quick-add-button hover-reveal rule in theme.css); clicking it reveals the dropdown - built on
// the theme's existing x-listbox for row selection, but a plain absolutely-positioned box (not x-popover) for
// the dropdown itself, since x-popover reparents to <body> and switches to a fullscreen bottom-sheet layout
// on mobile only, which is exactly what made it behave differently (and break) there vs desktop - plus an
// "Add to cart" button and a close button to back out. Picking a variant only resolves/enables "Add to cart"
// and swaps its label to "Sold out" when unavailable - it never submits on its own, and it never closes the
// drawer itself; the drawer only closes once "cart:change" confirms an item was actually added (see
// slideshow-split-content.liquid).
class ProductCardVariantSelect extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.toggleButton = this.querySelector("[data-variant-select-toggle]");
    this.closeButton = this.querySelector("[data-variant-select-close]");
    this.panel = this.querySelector(".product-card__variant-select-panel");
    this.dropdownTrigger = this.querySelector("[data-variant-select-trigger]");
    this.dropdown = this.querySelector("[data-variant-select-popover]");
    this.listbox = this.querySelector("x-listbox");
    this.triggerLabel = this.querySelector("[data-variant-select-trigger-label]");
    this.idInput = this.querySelector("[data-variant-select-id]");
    this.submitButton = this.querySelector("[data-variant-select-submit]");
    this.submitButtonLabel = this.querySelector("[data-variant-select-submit-label]");

    this.placeholderText = this.dataset.placeholderText || this.triggerLabel?.textContent || "";
    this.addToCartText = this.dataset.addToCartText || this.submitButtonLabel?.textContent || "";
    this.soldOutText = this.dataset.soldOutText || this.addToCartText;

    const { signal } = this.abortController;

    this.toggleButton?.addEventListener("click", () => this.revealPanel(), { signal });
    this.closeButton?.addEventListener("click", () => this.reset(), { signal });
    this.dropdownTrigger?.addEventListener("click", () => this.toggleDropdown(), { signal });
    this.addEventListener("listbox:select", () => this.closeDropdown(), { signal });
    this.addEventListener("listbox:change", (event) => this.onVariantChange(event), { signal });
    document.addEventListener("click", (event) => this.onOutsideClick(event), { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  revealPanel() {
    if (!this.toggleButton || !this.panel) {
      return;
    }

    this.toggleButton.hidden = true;
    this.panel.hidden = false;
  }

  toggleDropdown() {
    if (!this.dropdown) {
      return;
    }

    if (this.dropdown.hidden) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  openDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdown.hidden = false;
    this.dropdownTrigger?.setAttribute("aria-expanded", "true");
  }

  closeDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdown.hidden = true;
    this.dropdownTrigger?.setAttribute("aria-expanded", "false");
  }

  onOutsideClick(event) {
    if (!this.dropdown || this.dropdown.hidden) {
      return;
    }

    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  reset() {
    if (this.toggleButton) {
      this.toggleButton.hidden = false;
    }

    if (this.panel && this.toggleButton) {
      this.panel.hidden = true;
    }

    this.closeDropdown();

    if (this.triggerLabel) {
      this.triggerLabel.textContent = this.placeholderText;
    }

    if (this.idInput) {
      this.idInput.value = "";
    }

    // Set to "" rather than removed: x-listbox's attributeChangedCallback ignores the first change away
    // from a null attribute, so removing it here would silently swallow the next selection after a reset.
    this.listbox?.setAttribute("aria-activedescendant", "");
    this.querySelectorAll('[role="option"]').forEach((option) => {
      option.setAttribute("aria-selected", "false");
    });

    if (this.submitButton) {
      this.submitButton.disabled = true;
    }

    if (this.submitButtonLabel) {
      this.submitButtonLabel.textContent = this.addToCartText;
    }
  }

  onVariantChange(event) {
    const available = event.target?.dataset.variantAvailable === "true";

    if (this.submitButton) {
      this.submitButton.disabled = !available;
    }

    if (this.submitButtonLabel) {
      this.submitButtonLabel.textContent = available ? this.addToCartText : this.soldOutText;
    }
  }
}

if (!window.customElements.get("product-card-variant-select")) {
  window.customElements.define("product-card-variant-select", ProductCardVariantSelect);
}


// Bug fix: the theme's own <product-rerender> partial-swap (assets/theme.js) only swaps a fixed list of
// block types (sku, price, variant-picker, buy-buttons, etc.) on variant change. Two block types we've added
// custom per-variant content to aren't in that list, since the theme has no way to know they need it:
// - "title": shows the selected variant's Material value below the product name.
// - "product-meta": shows product.metafields.custom.product_custom_tag | the SELECTED VARIANT's
//   metafields.custom.made_to_order_messaging - the latter is per-variant, so it needs this too.
// "product:rerender" is dispatched directly on the product <form> without `bubbles: true` (see theme.js), so
// it has to be caught during the capture phase to reach it via a document listener.
const CUSTOM_RERENDER_BLOCK_TYPES = ["title", "product-meta"];

document.addEventListener(
  "product:rerender",
  (event) => {
    if (event.detail.productChange) {
      return;
    }

    const form = event.target;
    const productRerender = form.closest?.("product-rerender[allow-partial-rerender]");
    if (!productRerender) {
      return;
    }

    CUSTOM_RERENDER_BLOCK_TYPES.forEach((blockType) => {
      const newBlock = event.detail.htmlFragment.querySelector(`#${productRerender.id} [data-block-type="${blockType}"]`);
      const currentBlock = productRerender.querySelector(`[data-block-type="${blockType}"]`);

      if (newBlock && currentBlock) {
        currentBlock.replaceWith(newBlock);
      }
    });
  },
  true
);

// Picking a variant swaps its image with whatever is currently first, in place of the theme's own
// scroll/jump behavior (assets/theme.js). Strict two-element swap, not a reorder - everything else
// stays put no matter how many variants get clicked.
document.addEventListener(
  "variant:change",
  (event) => {
    // event.target.id would return the hidden <input name="id"> instead of the form's id attribute.
    const gallery = document.querySelector(`product-gallery[form="${event.target.getAttribute("id")}"]`);
    if (!gallery) {
      return;
    }

    event.stopPropagation();

    const targetMediaId = event.detail.variant?.featured_media?.id;
    if (!targetMediaId) {
      return;
    }

    const carousel = gallery.querySelector(".product-gallery__carousel");
    const thumbnailScroller = gallery.querySelector(".product-gallery__thumbnail-scroller");

    [carousel, thumbnailScroller].forEach((list) => {
      const target = list?.querySelector(`[data-media-id="${targetMediaId}"]`);
      const current = list?.firstElementChild;
      if (!target || !current || current === target) {
        return;
      }
      const placeholder = document.createComment("");
      current.replaceWith(placeholder);
      target.replaceWith(current);
      placeholder.replaceWith(target);
      list.scrollLeft = 0;
      list.scrollTop = 0;
    });

    carousel?.select(0, { instant: true });

    thumbnailScroller?.querySelectorAll("[data-media-id]").forEach((button) => {
      button.setAttribute("aria-current", button.getAttribute("data-media-id") === String(targetMediaId) ? "true" : "false");
    });
  },
  true
);

(function () {
  const PAGE_SIZE = 4;

  function isGridLayout(gallery) {
    return (gallery.dataset.desktopLayout || "").includes("grid");
  }

  function isArrowsMobile(gallery) {
    return gallery.dataset.mobileControls === "arrows_full";
  }

  function isQuickBuyGallery(gallery) {
    return !!gallery.closest(".quick-buy-modal__gallery-wrapper");
  }

  function hasScrollbar(gallery) {
    return isArrowsMobile(gallery) || isQuickBuyGallery(gallery);
  }


  function visibleMedia(gallery) {
    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return [];
    return Array.from(carousel.children).filter(
      (media) => media.classList.contains("product-gallery__media") && !media.hidden
    );
  }

  // The arrows should sit centered on the first ROW of the current page (e.g. the top-right image in a
  // 2-column grid), not centered across the whole 2-row block - grid-auto-rows sizes each row to its
  // tallest image, so this is measured from the actually-visible items rather than assumed from CSS.
  function firstRowHeight(gallery) {
    const items = visibleMedia(gallery).filter((item) => !item.classList.contains("product-gallery__media--paged-out"));
    if (!items.length) return null;

    const minTop = Math.min(...items.map((item) => item.offsetTop));
    const rowHeights = items.filter((item) => Math.abs(item.offsetTop - minTop) < 1).map((item) => item.offsetHeight);
    return rowHeights.length ? Math.max(...rowHeights) : null;
  }

  function positionNavButtons(gallery) {
    const rowHeight = firstRowHeight(gallery);
    if (rowHeight) {
      gallery.style.setProperty("--gallery-page-nav-top", `${rowHeight / 2}px`);
    }
  }

  function applyPage(gallery) {
    if (!isGridLayout(gallery)) return;

    const media = visibleMedia(gallery);
    const pageCount = Math.ceil(media.length / PAGE_SIZE);
    const clampedPage = Math.min(parseInt(gallery.dataset.galleryPage, 10) || 0, Math.max(pageCount - 1, 0));
    gallery.dataset.galleryPage = String(clampedPage);

    media.forEach((item, index) => {
      item.classList.toggle("product-gallery__media--paged-out", Math.floor(index / PAGE_SIZE) !== clampedPage);
    });

    const prevButton = gallery.querySelector("[data-gallery-page-prev]");
    const nextButton = gallery.querySelector("[data-gallery-page-next]");
    if (prevButton) prevButton.hidden = clampedPage <= 0;
    if (nextButton) nextButton.hidden = clampedPage >= pageCount - 1;

    positionNavButtons(gallery);
  }

  function ensureNavButtons(gallery) {
    if (!isGridLayout(gallery) || gallery.querySelector("[data-gallery-page-next]")) return;

    const imageList = gallery.querySelector(".product-gallery__image-list");
    if (!imageList) return;

    const prevButton = document.createElement("button");
    prevButton.type = "button";
    prevButton.hidden = true;
    prevButton.setAttribute("data-gallery-page-prev", "");
    prevButton.className =
      "product-gallery__page-nav product-gallery__page-nav--prev circle-button circle-button--sm hover:animate-icon-inline";
    prevButton.innerHTML =
      '<span class="sr-only">' +
      (window.themeVariables?.strings?.previous || "Previous") +
      '</span><svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>';

    const nextButton = document.createElement("button");
    nextButton.type = "button";
    nextButton.hidden = true;
    nextButton.setAttribute("data-gallery-page-next", "");
    nextButton.className =
      "product-gallery__page-nav product-gallery__page-nav--next circle-button circle-button--sm hover:animate-icon-inline";
    nextButton.innerHTML =
      '<span class="sr-only">' +
      (window.themeVariables?.strings?.next || "Next") +
      '</span><svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>';

    imageList.append(prevButton, nextButton);
  }

  function ensureMobileNavButtons(gallery) {
    if (!hasScrollbar(gallery) || gallery.querySelector("[data-gallery-mobile-next]")) return;

    const imageList = gallery.querySelector(".product-gallery__image-list");
    if (!imageList) return;

    const prevButton = document.createElement("button");
    prevButton.type = "button";
    prevButton.hidden = true;
    prevButton.setAttribute("data-gallery-mobile-prev", "");
    prevButton.className =
      "product-gallery__mobile-nav product-gallery__mobile-nav--prev circle-button circle-button--sm hover:animate-icon-inline";
    prevButton.innerHTML =
      '<span class="sr-only">' +
      (window.themeVariables?.strings?.previous || "Previous") +
      '</span><svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>';

    const nextButton = document.createElement("button");
    nextButton.type = "button";
    nextButton.hidden = true;
    nextButton.setAttribute("data-gallery-mobile-next", "");
    nextButton.className =
      "product-gallery__mobile-nav product-gallery__mobile-nav--next circle-button circle-button--sm hover:animate-icon-inline";
    nextButton.innerHTML =
      '<span class="sr-only">' +
      (window.themeVariables?.strings?.next || "Next") +
      '</span><svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>';

    imageList.append(prevButton, nextButton);
  }

  function updateMobileNav(gallery) {
    if (!hasScrollbar(gallery)) return;

    const carousel = gallery.querySelector(".product-gallery__carousel");
    const prevButton = gallery.querySelector("[data-gallery-mobile-prev]");
    const nextButton = gallery.querySelector("[data-gallery-mobile-next]");
    if (!carousel || !prevButton || !nextButton) return;

    const cellCount = carousel.cells.length;
    if (cellCount <= 1) {
      prevButton.hidden = true;
      nextButton.hidden = true;
      return;
    }

    const index = carousel.selectedIndex;
    prevButton.hidden = index <= 0;
    nextButton.hidden = index >= cellCount - 1;
  }

  function observeMobileNav(gallery) {
    if (gallery.dataset.galleryMobileNavObserved) return;
    gallery.dataset.galleryMobileNavObserved = "true";

    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    carousel.addEventListener("carousel:change", () => updateMobileNav(gallery));
  }

  function ensureScrollbar(gallery) {
    if (!hasScrollbar(gallery) || gallery.querySelector("[data-gallery-scrollbar]")) return;

    const imageList = gallery.querySelector(".product-gallery__image-list");
    if (!imageList) return;

    const track = document.createElement("div");
    track.setAttribute("data-gallery-scrollbar", "");
    track.setAttribute("aria-hidden", "true");
    track.className = "product-gallery__scrollbar";
    track.hidden = true;

    const thumb = document.createElement("div");
    thumb.className = "product-gallery__scrollbar-thumb";
    track.appendChild(thumb);

    imageList.appendChild(track);
  }

  // Recomputes the thumb's width/position from the carousel's current scroll geometry. Safe to call any
  // time the carousel's width, scroll offset, or slide count could have changed - it just re-measures.
  function updateScrollbar(gallery) {
    if (!hasScrollbar(gallery)) return;

    const carousel = gallery.querySelector(".product-gallery__carousel");
    const track = gallery.querySelector("[data-gallery-scrollbar]");
    const thumb = track?.querySelector(".product-gallery__scrollbar-thumb");
    if (!carousel || !track || !thumb) return;

    if (carousel.cells.length <= 1) {
      track.hidden = true;
      return;
    }

     track.hidden = false;

    const trackWidth = track.clientWidth;
    if (!trackWidth || !carousel.scrollWidth) return;

    const visibleRatio = Math.min(1, carousel.clientWidth / carousel.scrollWidth);
    const thumbWidth = Math.max(trackWidth * visibleRatio, 24);
    const scrollableWidth = carousel.scrollWidth - carousel.clientWidth;
    // Math.abs: RTL browsers can report scrollLeft as 0/negative - inset-inline-start (below) is a logical
    // property so this magnitude-only progress value renders correctly in both directions without a
    // document.dir branch here.
    const progress = scrollableWidth > 0 ? Math.min(1, Math.abs(carousel.scrollLeft) / scrollableWidth) : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.insetInlineStart = `${progress * (trackWidth - thumbWidth)}px`;
  }

  // Recomputes on: every carousel scroll frame (drag feedback), settle/select (variant-swap jumps), and
  // any width change of the carousel itself - a ResizeObserver is the one thing that also transparently
  // covers a product accordion (snippets/product-info.liquid, theme.js AccordionDisclosure) opening or
  // closing nearby and shifting the gallery's own box, without needing to special-case that trigger.
  function observeScrollbar(gallery) {
    if (gallery.dataset.galleryScrollbarObserved) return;
    gallery.dataset.galleryScrollbarObserved = "true";

    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    let scrollFrame = null;
    carousel.addEventListener("scroll", () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        updateScrollbar(gallery);
      });
    }, { passive: true });

    carousel.addEventListener("carousel:change", () => updateScrollbar(gallery));
    carousel.addEventListener("carousel:settle", () => updateScrollbar(gallery));
    new ResizeObserver(() => updateScrollbar(gallery)).observe(carousel);
  }

  function enableScrollbarInteraction(gallery) {
    if (gallery.dataset.galleryScrollbarInteractive) return;
    gallery.dataset.galleryScrollbarInteractive = "true";

    const carousel = gallery.querySelector(".product-gallery__carousel");
    const track = gallery.querySelector("[data-gallery-scrollbar]");
    if (!carousel || !track) return;

    let lastIndex = -1;

    function selectFromClientX(clientX) {
      const rect = track.getBoundingClientRect();
      if (!rect.width || carousel.cells.length <= 1) return;

      const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const targetFraction = document.dir === "rtl" ? 1 - fraction : fraction;
      const index = Math.round(targetFraction * (carousel.cells.length - 1));

      if (index === lastIndex) return;
      lastIndex = index;
      carousel.select(index, { instant: true });
    }

    track.addEventListener("pointerdown", (event) => {
      lastIndex = -1;
      track.setPointerCapture(event.pointerId);
      selectFromClientX(event.clientX);
    });

    track.addEventListener("pointermove", (event) => {
      if (!track.hasPointerCapture(event.pointerId)) return;
      selectFromClientX(event.clientX);
    });

    ["pointerup", "pointercancel"].forEach((type) => {
      track.addEventListener(type, (event) => {
        if (track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
      });
    });
  }


  // Media visibility also changes outside of a plain click (variant-specific filtering re-applies the
  // "hidden" attribute via the theme's own carousel.filter(), see theme.js onSectionRerender_fn), so the
  // page is recomputed reactively rather than only in response to the click/variant-change handlers below.
  function observeVisibilityChanges(gallery) {
    if (gallery.dataset.galleryPaginationObserved) return;
    gallery.dataset.galleryPaginationObserved = "true";

    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    new MutationObserver(() => {
      applyPage(gallery);
      updateMobileNav(gallery);
    }).observe(carousel, {
      attributes: true,
      attributeFilter: ["hidden"],
      subtree: true,
    });
  }

  // Row height also shifts once images actually finish loading (before that, their reserved space may
  // differ slightly) and on viewport resize, so it's kept in sync separately from applyPage's own calls.
  function observeRowHeight(gallery) {
    if (gallery.dataset.galleryRowHeightObserved) return;
    gallery.dataset.galleryRowHeightObserved = "true";

    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    new ResizeObserver(() => positionNavButtons(gallery)).observe(carousel);
  }

  function initGallery(gallery) {
    ensureNavButtons(gallery);
    ensureMobileNavButtons(gallery);
    ensureScrollbar(gallery);
    observeVisibilityChanges(gallery);
    observeRowHeight(gallery);
    observeMobileNav(gallery);
    observeScrollbar(gallery);
    enableScrollbarInteraction(gallery);
    applyPage(gallery);
    updateMobileNav(gallery);
    updateScrollbar(gallery);
  }

  function initAll(root) {
    (root || document).querySelectorAll("product-gallery").forEach(initGallery);
  }

  document.addEventListener("click", (event) => {
    const prevButton = event.target.closest("[data-gallery-page-prev]");
    const nextButton = event.target.closest("[data-gallery-page-next]");
    const button = prevButton || nextButton;
    if (button) {
      const gallery = button.closest("product-gallery");
      if (!gallery) return;

      const currentPage = parseInt(gallery.dataset.galleryPage, 10) || 0;
      gallery.dataset.galleryPage = String(prevButton ? Math.max(0, currentPage - 1) : currentPage + 1);
      applyPage(gallery);
      return;
    }

    const mobilePrevButton = event.target.closest("[data-gallery-mobile-prev]");
    const mobileNextButton = event.target.closest("[data-gallery-mobile-next]");
    const mobileButton = mobilePrevButton || mobileNextButton;
    if (!mobileButton) return;

    const carousel = mobileButton.closest("product-gallery")?.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    if (mobilePrevButton) {
      carousel.previous();
    } else {
      carousel.next();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("dialog:after-show", (event) => initAll(event.target), true);

  document.addEventListener(
    "variant:change",
    (event) => {
      // event.target.id would return the hidden <input name="id"> instead of the form's id attribute.
      const gallery = document.querySelector(`product-gallery[form="${event.target.getAttribute("id")}"]`);
      if (!gallery) return;

      gallery.dataset.galleryPage = "0";
      applyPage(gallery);
      updateMobileNav(gallery);
      updateScrollbar(gallery);
    },
    true
  );

  document.addEventListener(
    "toggle",
    (event) => {
      if (!(event.target instanceof HTMLDetailsElement)) return;

      document.querySelectorAll("product-gallery").forEach((gallery) => {
        if (!hasScrollbar(gallery)) return;
        updateScrollbar(gallery);
        setTimeout(() => updateScrollbar(gallery), 450);
      });
    },
    true
  );
})();

document.addEventListener(
  "variant:change",
  (event) => {
    // event.target.id would return the hidden <input name="id"> instead of the form's id attribute.
    const mainFormId = event.target.getAttribute("id");
    const paymentTermsForm = document.getElementById(`${mainFormId}-payment-installment`);
    const paymentTermsBlock = paymentTermsForm?.closest('.product-info__block-item[data-block-type="payment-terms"]');

    if (paymentTermsBlock) {
      paymentTermsBlock.hidden = !event.detail.variant?.available;
    }
  },
  true
);

// PDP "Customer Reviews" heading: used to be a static rich-text section rendered above the Okendo reviews
// app block, but that section had no way to know whether the product actually had reviews, so it showed
// up even for products with zero reviews. Okendo mounts the widget client-side and writes a JSON script
// tag with the real review count once it finishes rendering, so the heading is injected here - directly
// into the widget itself - and only once that data confirms there's something to show.
(function () {
  function updateReviewsWidget(dataScript) {
    const section = dataScript.closest(".shopify-section");
    const container = dataScript.closest(".shopify-app-block")?.querySelector("[data-oke-container]");
    if (!container) {
      return;
    }

    let reviewCount = 0;
    try {
      reviewCount = JSON.parse(dataScript.textContent).reviewAggregate?.ratingAndReviewCount || 0;
    } catch (error) {
      return;
    }

    if (section) {
      section.hidden = reviewCount < 1;
    }

    if (reviewCount < 1 || container.querySelector(".pdp-reviews-heading")) {
      return;
    }

    const heading = document.createElement("p");
    heading.className = "h6 pdp-reviews-heading";
    heading.textContent = "Customer Reviews";
    container.prepend(heading);
  }

  function initAll(root) {
    (root || document).querySelectorAll("script[data-oke-metafield-data]").forEach(updateReviewsWidget);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) {
          return;
        }

        if (node.matches?.("script[data-oke-metafield-data]")) {
          updateReviewsWidget(node);
        }

        node.querySelectorAll?.("script[data-oke-metafield-data]").forEach(updateReviewsWidget);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// Toggles the pre-rendered single/pair price markup from price-list.liquid ("show_quantity_pair_pricing").
// Shared by QuantityPairSelector and QuantityPairLrpController below.
function setQuantityPairPriceMode(isPair) {
  document.querySelectorAll("price-list[data-quantity-pair-pricing]").forEach((priceList) => {
    priceList.querySelectorAll('[data-quantity-mode="single"]').forEach((el) => {
      el.style.display = isPair ? "none" : "contents";
    });
    priceList.querySelectorAll('[data-quantity-mode="pair"]').forEach((el) => {
      el.style.display = isPair ? "contents" : "none";
    });
  });
}

// Swaps the PDP heading between the regular title and the "custom.pair_title" metafield (rendered as
// data-single-title / data-pair-title in snippets/product-info.liquid). No-op when the product has no pair title.
function setQuantityPairTitle(isPair) {
  document.querySelectorAll("h1[data-pair-title]").forEach((heading) => {
    heading.textContent = isPair ? heading.dataset.pairTitle : heading.dataset.singleTitle;
  });
}

class QuantityPairSelector extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.optionInputs = this.querySelectorAll("[data-quantity-pair-option-input]");
    this.triggerLabel = this.querySelector("[data-quantity-pair-trigger-label]");
    this.selectedLabel = this.querySelector("[data-quantity-pair-selected-label]");
    this.form = document.forms[this.getAttribute("form")];
    this.hiddenQuantityInput = this.querySelector("[data-quantity-pair-quantity-input]");
    this.propertyInputs = this.querySelectorAll("[data-quantity-pair-property-input]");

    if (!this.optionInputs.length || !this.form) {
      return;
    }

    // Only wire up our own hidden quantity input as the form's submitted quantity if nothing else already
    // owns that name (e.g. a merchant-added "quantity_selector" block).
    if (!this.findExternalQuantityInput() && this.hiddenQuantityInput) {
      this.hiddenQuantityInput.name = "quantity";
      this.hiddenQuantityInput.setAttribute("form", this.getAttribute("form"));
    }

    this.optionInputs.forEach((input) => input.addEventListener("change", this.onChange.bind(this), { signal }));

    document.addEventListener(
      "product:rerender",
      (event) => {
        // event.target.id would return the hidden <input name="id"> instead of the form's id attribute.
        if (event.detail.productChange || event.target.getAttribute("id") !== this.getAttribute("form")) {
          return;
        }
        // The variant re-render resets the form's quantity to 1, so re-apply the selected option (quantity,
        // property and labels) rather than only the price/title - otherwise Pair is shown but 1 item is added.
        queueMicrotask(() => {
          const checkedInput = Array.from(this.optionInputs).find((input) => input.checked);
          if (checkedInput) {
            this.applyValue(checkedInput);
          }
        });
      },
      { signal, capture: true }
    );

    const checkedInput = Array.from(this.optionInputs).find((input) => input.checked);
    if (checkedInput) {
      this.applyValue(checkedInput);
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  // Looked up on every use, not cached: the theme replaces the quantity_selector block on each variant change
  // (see ProductRerender in assets/theme.js), which would leave a cached reference pointing at a detached input.
  findExternalQuantityInput() {
    return Array.from(this.form.elements).find(
      (element) => element.name === "quantity" && element !== this.hiddenQuantityInput
    );
  }

  currentValue() {
    return Array.from(this.optionInputs).find((input) => input.checked)?.value;
  }

  onChange(event) {
    this.applyValue(event.target);
  }

  applyValue(input) {
    const isPair = input.value === "pair";
    const quantity = isPair ? 2 : 1;
    const label = input.dataset.label || input.value;

    if (this.selectedLabel) {
      this.selectedLabel.textContent = label;
    }

    if (this.triggerLabel) {
      this.triggerLabel.textContent = label;
    }

    const externalQuantityInput = this.findExternalQuantityInput();
    if (externalQuantityInput) {
      externalQuantityInput.value = quantity;
      externalQuantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (this.hiddenQuantityInput) {
      this.hiddenQuantityInput.value = quantity;
    }

    this.propertyInputs.forEach((propertyInput) => {
      propertyInput.value = label;
    });

    setQuantityPairPriceMode(isPair);
    setQuantityPairTitle(isPair);
  }
}

if (!window.customElements.get("quantity-pair-selector")) {
  window.customElements.define("quantity-pair-selector", QuantityPairSelector);
}

class QuantityPairLrpController {
  constructor() {
    // formId -> { variantIds: number[], label: string, optionPosition: string }
    this.activeForms = new Map();

    document.addEventListener("click", this.onTriggerClick.bind(this));
    // The trigger is a <label role="button">, not a real <button> (see variant-picker.liquid for why), so
    // Enter/Space needs to be wired up manually for keyboard users.
    document.addEventListener("keydown", this.onTriggerKeydown.bind(this));
    const onRealOptionInteraction = this.onOptionInputChange.bind(this);
    document.addEventListener("click", onRealOptionInteraction, true);
    document.addEventListener("change", onRealOptionInteraction, true);
    // "product:rerender" rather than "variant:change" - see the comment on this same substitution in
    // QuantityPairSelector above.
    document.addEventListener("product:rerender", this.onProductRerender.bind(this), true);
    document.addEventListener("submit", this.onSubmit.bind(this), true);

    if (document.readyState === "complete") {
      this.applyDefault();
    } else {
      document.addEventListener("DOMContentLoaded", () => this.applyDefault());
    }
  }

  // "Pair" is the default selection - applied once on initial page load for whichever "BUYAS: LRP" product
  // happens to be on the page (there is at most one per PDP).
  applyDefault() {
    const trigger = document.querySelector("[data-quantity-pair-mode-trigger]");
    if (trigger) {
      this.activate(trigger, { closePopover: false });
    }
  }

  onTriggerClick(event) {
    const trigger = event.target.closest("[data-quantity-pair-mode-trigger]");
    if (!trigger) {
      return;
    }

    event.preventDefault();
    this.activate(trigger);
  }

  onTriggerKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const trigger = event.target.closest("[data-quantity-pair-mode-trigger]");
    if (!trigger) {
      return;
    }

    event.preventDefault();
    this.activate(trigger);
  }

  activate(trigger, { closePopover = true } = {}) {
    const formId = trigger.dataset.form;
    const variantIds = (trigger.dataset.variantIds || "")
      .split(",")
      .filter(Boolean)
      .map(Number);
    const label = trigger.dataset.label || trigger.textContent.trim();
    const optionPosition = trigger.dataset.optionPosition;

    if (!formId || variantIds.length === 0) {
      return;
    }

    this.activeForms.set(formId, { variantIds, label, optionPosition });
    this.applySelectedLook(trigger, label);

    setQuantityPairPriceMode(true);
    setQuantityPairTitle(true);
    document.dispatchEvent(new CustomEvent("quantity-pair:change", { detail: { formId, isPair: true, label } }));

    if (closePopover) {
      trigger.closest("x-popover")?.hide();
    }
  }

  applySelectedLook(trigger, label) {
    trigger.setAttribute("aria-selected", "true");
    // Only meaningful for the block-swatch rendering (":checked + .block-swatch, .block-swatch.is-selected" in
    // theme.css) - inert for the dropdown popover, which keys its own selected look off [aria-selected] instead.
    trigger.classList.add("is-selected");

    // Resolving by form-id works regardless of where the popover currently lives.
    const variantPicker = document.querySelector(`variant-picker[form-id="${CSS.escape(trigger.dataset.form)}"]`);
    const triggerLabel = variantPicker?.querySelector("[data-quantity-pair-trigger-label]");
    if (triggerLabel) {
      triggerLabel.textContent = label;
    }
  }

  onOptionInputChange(event) {
    const input = event.target;
    if (!input.matches?.("input[data-option-position]")) {
      return;
    }

    const formId = input.getAttribute("form");
    const pairState = formId && this.activeForms.get(formId);
    if (!pairState || input.dataset.optionPosition !== pairState.optionPosition) {
      return;
    }

    this.deactivate(formId);
  }

  deactivate(formId) {
    this.activeForms.delete(formId);
    setQuantityPairPriceMode(false);
    setQuantityPairTitle(false);
    document.dispatchEvent(new CustomEvent("quantity-pair:change", { detail: { formId, isPair: false } }));

    const variantPicker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    const trigger = variantPicker?.querySelector("[data-quantity-pair-mode-trigger]");
    if (trigger) {
      trigger.setAttribute("aria-selected", "false");
      trigger.classList.remove("is-selected");
    }
  }

  onProductRerender(event) {
    // event.target.id would return the hidden <input name="id"> instead of the form's id attribute.
    const formId = event.target?.getAttribute("id");
    if (event.detail.productChange || !formId) {
      return;
    }

    const pairState = this.activeForms.get(formId);

    queueMicrotask(() => {
      if (!pairState) {
        // "BUYAS: Pair" products are driven by <quantity-pair-selector>, which already re-applies the price/title
        // mode on this same event - resetting to single here would override it.
        if (!document.querySelector("quantity-pair-selector")) {
          setQuantityPairPriceMode(false);
          setQuantityPairTitle(false);
        }
        return;
      }

      const variantPicker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
      const trigger = variantPicker?.querySelector("[data-quantity-pair-mode-trigger]");
      if (trigger) {
        // The re-rendered trigger carries the Left/Right variant ids for the newly selected options (e.g. the new
        // Material). Without refreshing them, Pair would keep adding the variants captured when it was clicked.
        const variantIds = (trigger.dataset.variantIds || "").split(",").filter(Boolean).map(Number);
        if (variantIds.length > 0) {
          this.activeForms.set(formId, { ...pairState, variantIds });
        }
        this.applySelectedLook(trigger, pairState.label);
      }
      setQuantityPairPriceMode(true);
      setQuantityPairTitle(true);
    });
  }

  onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    // form.id would return the hidden <input name="id"> instead of the form's id attribute.
    const pairState = this.activeForms.get(form.getAttribute("id"));
    if (!pairState) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.submitPairToCart(form, pairState);
  }

  async submitPairToCart(form, { variantIds, label }) {
    const submitButtons = Array.from(form.elements).filter((element) => element.type === "submit");
    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    let response;
    let responseJson;
    try {
      response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          // "_Quantity Type": "_" prefix hides it from customer-facing displays (cart drawer, order confirmation)
          // while it stays visible in the admin order view; cart grouping keys off it. "Quantity Type" is the
          // customer-visible twin.
          items: variantIds.map((id) => ({
            id,
            quantity: 1,
            properties: { "_Quantity Type": label, "Quantity Type": label },
          })),
          sections: sectionsToBundle.join(","),
          sections_url: `${Shopify.routes.root}variants/${variantIds[0]}`,
        }),
      });
      responseJson = await response.json();
    } finally {
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }

    if (response.ok) {
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;
      form.dispatchEvent(
        new CustomEvent("variant:add", { bubbles: true, detail: { items: responseJson.items, cart: cartContent } })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart: cartContent } })
      );
    } else {
      form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: responseJson.description } }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }
}

new QuantityPairLrpController();

class QuantityPairCartGroupController {
  constructor() {
    document.addEventListener("click", this.onClick.bind(this));
  }

  onClick(event) {
    const actionElement = event.target.closest("[data-quantity-pair-action]");
    if (!actionElement) {
      return;
    }

    const container = actionElement.closest("[data-quantity-pair-keys]");
    if (!container) {
      return;
    }

    event.preventDefault();

    const keys = container.dataset.quantityPairKeys.split(",");
    const quantities = container.dataset.quantityPairQuantities.split(",").map(Number);
    const action = actionElement.dataset.quantityPairAction;

    let newQuantities;
    if (action === "remove") {
      newQuantities = quantities.map(() => 0);
    } else if (action === "increase") {
      newQuantities = quantities.map((quantity) => quantity + 1);
    } else if (action === "decrease") {
      // Each side of a pair never goes below 1 on its own - going lower isn't "half a pair", it's a removal.
      if (quantities.some((quantity) => quantity <= 1)) {
        return;
      }
      newQuantities = quantities.map((quantity) => quantity - 1);
    } else {
      return;
    }

    this.applyUpdate(keys, newQuantities);
  }

  async applyUpdate(keys, quantities) {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const updates = {};
    keys.forEach((key, index) => {
      updates[key] = quantities[index];
    });

    let response;
    let cartContent;
    try {
      response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates, sections: sectionsToBundle.join(",") }),
      });
      cartContent = await response.json();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return;
    }

    if (window.themeVariables.settings.pageType === "cart") {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "line-item:change", cart: cartContent } })
    );
  }
}

new QuantityPairCartGroupController();

// Header cart badge visibility: .header__cart-badge reuses the theme's own <cart-count> (assets/theme.js) for
// its live text, but that component only ever updates textContent - it has no show/hide behavior, unlike the
// <cart-dot> it replaced (which toggled "is-visible" itself). Mirrors that same toggle here instead of forking
// <cart-count>, using the same cart:change/cart:refresh/pageshow events and item_count check <cart-dot> used.
(function () {
  function toggleBadges(itemCount) {
    document.querySelectorAll(".header__cart-badge").forEach((badge) => {
      badge.classList.toggle("is-visible", itemCount > 0);
    });
  }

  async function toggleFromServer() {
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    toggleBadges(cart.item_count);
  }

  document.addEventListener("cart:change", (event) => toggleBadges(event.detail.cart.item_count));
  document.addEventListener("cart:refresh", toggleFromServer);
  window.addEventListener("pageshow", toggleFromServer);
})();

// Header cart count: overrides the theme's own <cart-count> (assets/theme.js), which shows the TOTAL
// QUANTITY across the cart (cart.item_count) - this shows the number of distinct LINE ITEMS instead
// (cart.items.length), per merchant preference (e.g. 2 of the same product is one line, shown as "1";
// 1 each of two different products is shown as "2"). Reuses the exact same cart:change/cart:refresh/
// pageshow events <cart-count> itself listens for (assets/theme.js), registered here AFTER its own
// listeners since this script loads later and listeners for the same event fire in registration order
// - so this always has the final say over what gets displayed, matching the header.liquid server
// render (cart.items.size) for the initial page load.
(function () {
  function applyLineItemCount(count) {
    document.querySelectorAll("cart-count").forEach((element) => {
      element.innerText = count;
    });
  }

  async function applyFromServer() {
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    applyLineItemCount(cart.items.length);
  }

  document.addEventListener("cart:change", (event) => applyLineItemCount(event.detail.cart.items.length));
  document.addEventListener("cart:refresh", applyFromServer);
  window.addEventListener("pageshow", applyFromServer);
})();

// Header predictive search default state ("Trending Now" / "JF Favorites"): the theme's own <predictive-search>
// component (assets/theme.js) clears its results slot to "" whenever the query is emptied - on form reset, and
// internally whenever the input is backspaced down to nothing - with no concept of a default, pre-query state.
// snippets/header-search.liquid renders that merchandiser-configured default markup into a sibling <template>;
// this restores it into the results slot any time the slot is cleared while the query input is empty.
(function () {
  function restoreDefaultResults(content) {
    const searchElement = content.closest("predictive-search");
    const template = searchElement?.querySelector("template[data-default-results]");
    if (!template || content.children.length > 0) {
      return;
    }

    const form = document.querySelector(`[aria-owns="${CSS.escape(searchElement.id)}"]`);
    if (form?.elements.q?.value) {
      return;
    }

    content.appendChild(template.content.cloneNode(true));
  }

  function initAll(root) {
    (root || document).querySelectorAll(".predictive-search__content").forEach(restoreDefaultResults);
  }

  if (document.readyState !== "loading") {
    initAll();
  } else {
    document.addEventListener("DOMContentLoaded", () => initAll());
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.target.classList?.contains("predictive-search__content")) {
        restoreDefaultResults(mutation.target);
      }
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

(function () {
  function reinitRebuy() {
    if (window.Rebuy && typeof window.Rebuy.init === "function") {
      window.Rebuy.init();
      return true;
    }

    return false;
  }

  document.addEventListener("cart-drawer:refreshed", () => {
    if (reinitRebuy()) {
      return;
    }

    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      if (reinitRebuy() || attempts >= 40) {
        clearInterval(poll);
      }
    }, 250);
  });
})();

(function () {
  const CART_ENDPOINT_PATTERN = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/;
  const CART_ADD_ENDPOINT_PATTERN = /\/cart\/add(\.js)?(\?|$)/;
  const originalFetch = window.fetch;
  let lastCartEventAt = 0;

  document.addEventListener("cart:change", () => {
    lastCartEventAt = Date.now();
  });
  document.addEventListener("cart:refresh", () => {
    lastCartEventAt = Date.now();
  });

  window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : input?.url || "";
    const method = (init?.method || (input instanceof Request ? input.method : "GET") || "GET").toUpperCase();
    const promise = originalFetch.apply(this, arguments);

    if (method === "POST" && CART_ENDPOINT_PATTERN.test(url)) {
      const requestedAt = Date.now();
      const isAdd = CART_ADD_ENDPOINT_PATTERN.test(url);

      promise
        .then((response) => {
          if (!response.ok) {
            return;
          }

          setTimeout(() => {
            if (lastCartEventAt >= requestedAt) {
              return;
            }

            if (isAdd && window.themeVariables?.settings?.cartType === "drawer") {
              document.addEventListener(
                "cart-drawer:refreshed",
                () => document.querySelector("cart-drawer")?.show(),
                { once: true }
              );
            }

            document.dispatchEvent(new CustomEvent("cart:refresh"));
          }, 400);
        })
        .catch(() => {});
    }

    return promise;
  };
})();

// Cart drawer Rebuy tabs click handling (sections/cart-drawer.liquid): <x-tabs> (theme.js) has its own internal
(function () {
  document.addEventListener("click", (event) => {
    const button = event.target.closest?.('.cart-drawer__rebuy-tabs [role="tab"]');
    if (!button) {
      return;
    }

    const tabs = button.closest("x-tabs");
    if (!tabs) {
      return;
    }

    const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
    const index = buttons.indexOf(button);

    if (index !== -1) {
      tabs.selectedIndex = index;
    }
  });
})();

// Cart drawer Rebuy grid (sections/cart-drawer.liquid, custom.css ".rebuy-product-grid" rules)
(function () {
  function important(el, prop, value) {
    el.style.setProperty(prop, value, "important");
  }

  function forceGrid(grid) {
    const track = grid.querySelector(".splide__track");
    const list = grid.querySelector(".splide__list");
    const slides = grid.querySelectorAll(".splide__slide");

    if (!list) {
      return;
    }

    grid.querySelectorAll(".splide__arrows, .splide__pagination").forEach((el) => important(el, "display", "none"));

    if (track) {
      important(track, "overflow", "visible");
      important(track, "padding", "0");
    }

    important(list, "display", "grid");
    important(list, "grid-template-columns", "repeat(3, 1fr)");
    important(list, "gap", "1rem");
    important(list, "transform", "none");
    important(list, "width", "100%");

    slides.forEach((slide, index) => {
      important(slide, "width", "auto");
      important(slide, "min-width", "0");
      important(slide, "flex", "none");
      important(slide, "margin", "0");
      important(slide, "display", index < 6 ? "block" : "none");
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll(".cart-drawer__app-blocks .rebuy-product-grid").forEach(forceGrid);
  }

  if (document.readyState !== "loading") {
    initAll();
  } else {
    document.addEventListener("DOMContentLoaded", () => initAll());
  }

  window.addEventListener("resize", () => requestAnimationFrame(() => initAll()));

  let pending = false;

  new MutationObserver(() => {
    if (pending) {
      return;
    }

    pending = true;

    requestAnimationFrame(() => {
      pending = false;
      initAll();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// Move the product description below the Add to Cart button on mobile
// In Product Inner page Move the product description below the Add to Cart button on mobile

document.addEventListener("DOMContentLoaded", function () {
    if (window.innerWidth <= 999.98) {
        const description = document.querySelector(
            '.product-info__block-item[data-block-type="description"]'
        );

        const buyButtons = document.querySelector(
            '.product-info__block-item[data-block-type="payment-terms"]'
        );

        if (description && buyButtons) {
            buyButtons.insertAdjacentElement("afterend", description);
        }
    }
});


// Update the close icon size and stroke width inside the cart drawer shadow DOM
document.querySelectorAll('cart-drawer, x-drawer').forEach(drawer => {
  const shadow = drawer.shadowRoot;
  if (!shadow) return;
  const closeIcon = shadow.querySelector('svg.icon.icon-close');
  if (closeIcon) {
    closeIcon.setAttribute('width', '16');
    closeIcon.setAttribute('height', '16');
    closeIcon.querySelectorAll('path').forEach(path => {
      path.setAttribute('stroke-width', '1');
    });
  }
});

// Keep the "Purchase Type" row in sync with the customer's live selection in the
// Skio subscription widget (fieldset.skio-plan-picker), which Skio renders on its own. Also
// inserts a reassurance note under the frequency dropdown ("Never run out. Cancel or pause
// anytime.") 
(function () {
  function updatePurchaseTypeRow(row) {
    const picker = document.querySelector(
      `fieldset.skio-plan-picker[skio-plan-picker="${row.dataset.productId}"]`
    );
    const valueEl = row.querySelector("[data-purchase-type-value]");
    if (!picker || !valueEl) return;

    const checked = picker.querySelector(".skio-group-input:checked");
    const isSubscription = !!checked?.hasAttribute("skio-selling-plan-group");
    valueEl.textContent = isSubscription ? row.dataset.subscribeLabel : row.dataset.oneTimeLabel;
  }

  function insertSubscriptionNote(row) {
    const note = row.dataset.subscriptionNote;
    if (!note) return;

    const picker = document.querySelector(
      `fieldset.skio-plan-picker[skio-plan-picker="${row.dataset.productId}"]`
    );
    if (!picker) return;

    picker.querySelectorAll(".skio-group-content").forEach((content) => {
      if (content.querySelector("[data-skio-subscription-note]")) return;

      const select = content.querySelector("select.skio-frequency");
      if (!select) return;

      const noteEl = document.createElement("p");
      noteEl.className = "text-subdued text-base";
      noteEl.setAttribute("data-skio-subscription-note", "");
      noteEl.textContent = note;
      select.insertAdjacentElement("afterend", noteEl);
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll("[data-purchase-type-row]").forEach((row) => {
      updatePurchaseTypeRow(row);
      insertSubscriptionNote(row);
    });
  }

  document.addEventListener("change", (event) => {
    if (!event.target.matches?.(".skio-group-input")) return;

    const productId = event.target.closest(".skio-plan-picker")?.getAttribute("skio-plan-picker");
    const row = document.querySelector(`[data-purchase-type-row][data-product-id="${productId}"]`);
    if (row) updatePurchaseTypeRow(row);
  });

  if (document.readyState !== "loading") {
    initAll();
  } else {
    document.addEventListener("DOMContentLoaded", () => initAll());
  }

  // Checks addedNodes (and their descendants) rather than mutation.target: for a childList mutation,
  // target is the PARENT a new node was inserted into, not the new node itself - so on the widget's
  // very first appearance, its freshly-inserted <fieldset class="skio-plan-picker"> is a CHILD of
  // target, not an ancestor of it, meaning target.closest(".skio-plan-picker") would still be null at
  // that exact moment and only start matching on some LATER, unrelated mutation happening inside the
  // widget after it already exists (see assets/OOS-logic.js for the same fix, same reasoning).
  new MutationObserver((mutations) => {
    const skioWidgetInserted = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node.matches(".skio-plan-picker") || node.querySelector(".skio-plan-picker"))
      )
    );
    if (skioWidgetInserted) {
      initAll();
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

// Keep the PDP "Quantity:" label (data-quantity-row) in sync with the quantity-selector's own
// input, which lives alongside it (see snippets/product-info.liquid). "change" covers the +/-
// buttons (which dispatch it manually), "input" covers the customer typing a value directly.
(function () {
  function updateQuantityValue(row) {
    const input = row.querySelector(".quantity-selector__input");
    const valueEl = row.querySelector("[data-quantity-value]");
    if (input && valueEl) valueEl.textContent = input.value;
  }

  ["input", "change"].forEach((eventType) => {
    document.addEventListener(eventType, (event) => {
      const input = event.target.closest(".quantity-selector__input");
      if (!input) return;

      const row = input.closest("[data-quantity-row]");
      if (row) updateQuantityValue(row);
    });
  });
})();

// Delivery Options / Pick Up in Store logic (snippets/product-info.liquid "delivery_options" block,
// the store selection drawer, cart-wide delivery attributes, cross-page preference, and the cart
// drawer's per-line pickup availability messages) all now live together in assets/pickup-in-store.js.


// Add a custom class to the body only on Shopify product detail pages
document.addEventListener('DOMContentLoaded', function () {
  const isProductPage = window.location.pathname.includes('/products/');
  if (isProductPage) {
    document.body.classList.add('product-detail-page');
  }
});

(function () {
  const MOBILE_QUERY = "(max-width: 999px)";
  const SCROLLED_CLASS = "header--search-scrolled";
  const SCROLL_THRESHOLD = 10;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function forEachHeader(callback) {
    document.querySelectorAll("x-header").forEach(callback);
  }

  function syncScrolledState() {
    forEachHeader((header) => {
      const scrolled = isMobile() && window.scrollY > SCROLL_THRESHOLD;
      header.classList.toggle(SCROLLED_CLASS, scrolled);
    });
  }

  window.addEventListener("scroll", syncScrolledState, { passive: true });
  window.addEventListener("resize", syncScrolledState);
  document.addEventListener("DOMContentLoaded", syncScrolledState);
  syncScrolledState();

  document.addEventListener(
    "click",
    (event) => {
      if (!isMobile()) {
        return;
      }

      const link = event.target.closest(".header__search-link a");
      const header = link?.closest("x-header");
      if (!link || !header?.classList.contains(SCROLLED_CLASS)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    true
  );
})();

(function () {
  const MOBILE_QUERY = "(max-width: 999px)";
  const SCROLLED_CLASS = "header--search-scrolled";
  const TRIGGER_SELECTOR = ".header__search-link";
  const SLOT_SELECTOR = ".header__mobile-search-bar";

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  const placements = new WeakMap();

  function moveIn(header) {
    if (placements.get(header)?.placed) {
      return;
    }
    const trigger = header.querySelector(TRIGGER_SELECTOR);
    const slot = header.querySelector(SLOT_SELECTOR);
    if (!trigger || !slot) {
      return;
    }
    placements.set(header, {
      originalParent: trigger.parentNode,
      originalNextSibling: trigger.nextSibling,
      placed: true,
    });
    slot.appendChild(trigger);
  }

  function moveOut(header) {
    const placement = placements.get(header);
    if (!placement?.placed) {
      return;
    }
    const trigger = header.querySelector(TRIGGER_SELECTOR);
    if (!trigger) {
      placement.placed = false;
      return;
    }
    if (placement.originalNextSibling?.isConnected) {
      placement.originalParent.insertBefore(trigger, placement.originalNextSibling);
    } else {
      placement.originalParent.appendChild(trigger);
    }
    placement.placed = false;
  }

  function sync() {
    document.querySelectorAll("x-header").forEach((header) => {
      if (isMobile() && !header.classList.contains(SCROLLED_CLASS)) {
        moveIn(header);
      } else {
        moveOut(header);
      }
    });
  }

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest(".header__search-link a");
      const header = trigger?.closest("x-header");
      if (!trigger || !isMobile() || header?.classList.contains(SCROLLED_CLASS)) {
        return;
      }

      const input = document.getElementById("searchInput");
      if (input) {
        setTimeout(() => input.focus(), 0);
      }
    },
    true
  );

  document.querySelectorAll("x-header").forEach((header) => {
    new MutationObserver(sync).observe(header, { attributes: true, attributeFilter: ["class"] });
  });

  window.addEventListener("resize", sync);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();


// Add "homepage" class to the body on the homepage
document.addEventListener('DOMContentLoaded', function () {
  if (window.location.pathname === '/') {
    document.body.classList.add('homepage');
  }
});

(function () {
  function forceHide() {
    document.querySelectorAll('[id="header-view-cart-button"]').forEach((button) => {
      if (button.style.display !== "none") {
        button.style.setProperty("display", "none", "important");
      }
    });
    requestAnimationFrame(forceHide);
  }

  requestAnimationFrame(forceHide);
})();

